import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Youtube, Instagram, Facebook } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.87a8.16 8.16 0 0 0 4.77 1.52V6.96a4.84 4.84 0 0 1-1.01-.27z"/>
  </svg>
);

const SocialIcon = ({ platform }) => {
  switch (platform) {
    case "youtube": case "youtubeShorts": return <Youtube size={18} />;
    case "tiktok": return <TikTokIcon size={18} />;
    case "instagram": return <Instagram size={18} />;
    case "facebook": return <Facebook size={18} />;
    default: return null;
  }
};

const SOCIAL_KEYS = [
  { key: "youtube", label: "YouTube", url: "https://www.youtube.com/channel/UCBS-wYB_X25spPaaUb5H2jA", followersKey: "youtubeMain" },
  { key: "youtubeShorts", label: "YouTube Shorts", url: "https://www.youtube.com/@georgeemadshorts", followersKey: "youtubeShorts" },
  { key: "tiktok", label: "TikTok", url: "https://www.tiktok.com/@georgeemad17", followersKey: "tiktok" },
  { key: "instagram", label: "Instagram", url: "https://www.instagram.com/georgeemad16/", followersKey: "instagram" },
  { key: "facebook", label: "Facebook", url: "https://www.facebook.com/georgeemad.tech.channel/", followersKey: "facebook" },
];

function formatNum(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return String(n);
}

export default function Footer() {
  const [followers, setFollowers] = useState(null);
  const [footerText, setFooterText] = useState("Tech Content Creator with 500K+ Audience Creating cinematic tech reviews, unboxing experiences, and creative short-form content that inspires millions.");

  useEffect(() => {
    axios.get(`${API}/followers`).then(r => setFollowers(r.data)).catch(() => {});
    axios.get(`${API}/content/about`).then(r => {
      if (r.data.footerText) setFooterText(r.data.footerText);
    }).catch(() => {});
  }, []);

  return (
    <footer data-testid="site-footer" className="border-t border-white/5 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <Link to="/" className="font-heading font-black text-2xl tracking-tight text-white">
              GEORGE<span className="text-[#E63946]">.</span>
            </Link>
            <p className="mt-4 text-sm text-zinc-500 leading-relaxed max-w-xs">
              {footerText}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-6">Navigation</h4>
            <div className="flex flex-col gap-3">
              {[
                { label: "Home", path: "/" },
                { label: "Portfolio", path: "/portfolio" },
                { label: "About", path: "/about" },
                { label: "Contact", path: "/contact" },
              ].map((link) => (
                <Link key={link.path} to={link.path} className="text-sm text-zinc-400 hover:text-white transition-colors duration-200">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-6">Follow Me</h4>
            <div className="flex flex-col gap-3">
              {SOCIAL_KEYS.map(({ key, label, url, followersKey }) => (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer" data-testid={`footer-social-${key}`}
                  className="flex items-center gap-3 text-sm text-zinc-400 hover:text-white transition-colors duration-200 group">
                  <span className="text-zinc-600 group-hover:text-[#E63946] transition-colors">
                    <SocialIcon platform={key} />
                  </span>
                  {label}
                  <span className="text-zinc-600 text-xs">
                    {followers ? formatNum(followers[followersKey]) : ""}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-600">&copy; {new Date().getFullYear()} George Emad. All rights reserved.</p>
          <p className="text-xs text-zinc-600">Designed with precision and passion.</p>
        </div>
      </div>
    </footer>
  );
}
